import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const Animals = [
    "cow.png", "dog.png", "cat.png", "horse.png", "goat.png", "sheep.png", "pig.png", "lion.png", "tiger.png", "elephant.png",
    "rhino.png", "giraffe.png", "zebra.png", "snake.png", "cheetah.png", "leopard.png", "wolf.png", "fox.png", "bear.png", "gorilla.png"
];

const AnimalNames = Animals.map(animal => animal.replace(".png", ""));

function shuffleArray<T>(array: T[]): T[] {
    return [...array].sort(() => Math.random() - 0.5);
}

type QuestionType = {
    text: string;
    correctAnimal: string;
    correctImage: string;
    allImages: string[];
};

const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    return { imageSize: screenWidth > 1024 ? 120 : screenWidth > 600 ? 150 : 80 };
};

const FindAnimal = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<string | null>(null);
    const [imageSize, setImageSize] = useState(getDynamicSizes().imageSize);
    const [showImage, setShowImage] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        const handleResize = () => setImageSize(getDynamicSizes().imageSize);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const shuffledAnimals = shuffleArray([...Animals]);
        const correctAnimal = AnimalNames[Math.floor(Math.random() * AnimalNames.length)];
        const correctImage = `${correctAnimal}.png`;

        const incorrectImages = shuffledAnimals.filter(img => img !== correctImage).slice(0, remainingImages);
        const allImages = shuffleArray([correctImage, ...incorrectImages]);

        return {
            text: `Select the image of: ${correctAnimal.toUpperCase()}`,
            correctAnimal,
            correctImage,
            allImages,
        };
    };

    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;

        setTimeout(() => {
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20)); console.log(columns)
            const rows = Math.floor(container.height / (imageSize + 20)); console.log(rows)
            const totalImage = columns * rows; console.log(totalImage)
            const remainingImages = totalImage > 1 ? totalImage : 0;

            setQuestion(generateRandomQuestion(remainingImages));
            setSelectedImage(null);
            setFeedback(null);
        }, 1000);
    }, [imageSize]);

    useEffect(() => {
        generateQuestion();
        document.title = "Find the Animal – Nursery Worksheet | Environmental Science | Mama’s Guidance"; // Set page title
    }, [generateQuestion]);

    const handleImageClick = (image: string) => {
        if (!question) return;

        setSelectedImage(image);
        setFeedback(image === question.correctImage ? "Right" : "Wrong");

        if (image === question.correctImage) {
            setShowImage(true)
            setTimeout(() => {
                setSelectedImage(null);
                setFeedback(null);
                setQuestion(null);
                generateQuestion();
                setShowImage(false)
            }, 5000);
        }
    };

    return (
        <div>
            <Helmet>
                <meta name="description" content='Engage nursery kids in learning about animals with the "Find the Animal" activity on Mamas Guidance—an interactive exercise to help children identify and recognize different animals while building observation and environmental awareness skills.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord  match-container" style={{ marginTop: "0%" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Find the Animal</h1>

                                <p>
                                    Introduce your little learners to the world of animals with this exciting <strong>Find the Animal</strong> worksheet! This nursery-level EVS (Environmental Science) activity helps children <strong>identify animals based on images or names</strong>, improving recognition skills, vocabulary, and observation.
                                </p>

                                <h2>🦁 What’s Inside the Worksheet?</h2>
                                <p>
                                    Children are presented with groups of animals in pictures. Their task is to <strong>find and circle or tick the correct animal</strong> based on the given name or clue (e.g., “Find the Lion” or “Which one is the Elephant?”).
                                </p>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Recognize common animals (<strong>domestic and wild</strong>)</li>
                                    <li>Improve <strong>visual identification</strong> and <strong>attention to detail</strong></li>
                                    <li>Learn <strong>animal names</strong> and <strong>appearances</strong></li>
                                    <li>Strengthen <strong>observation</strong> and <strong>categorization</strong> skills</li>
                                </ul>

                                <h2>👩‍🏫 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Say the animal name or read the instruction aloud to your child.</li>
                                    <li>Ask them to scan the pictures and find the correct one.</li>
                                    <li>Let them circle or color the animal they found.</li>
                                    <li>Encourage discussion: <strong>“Where have you seen this animal before?”</strong></li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/environmental-science/vegetables/find-vegetable"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Vegetable</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/fruit/find-fruit"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Fruit</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Identify Correct Body Parts</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Early science education begins with observation. Identifying animals not only builds vocabulary but also teaches children about their environment, nature, and living things. It&apos;s a fun and gentle way to start exploring the world!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Find the Animal</h1>

                            <p>
                                Introduce your little learners to the world of animals with this exciting <strong>Find the Animal</strong> worksheet! This nursery-level EVS (Environmental Science) activity helps children <strong>identify animals based on images or names</strong>, improving recognition skills, vocabulary, and observation.
                            </p>

                            <h2>🦁 What’s Inside the Worksheet?</h2>
                            <p>
                                Children are presented with groups of animals in pictures. Their task is to <strong>find and circle or tick the correct animal</strong> based on the given name or clue (e.g., “Find the Lion” or “Which one is the Elephant?”).
                            </p>

                            <h2>🎯 What Your Child Will Learn</h2>
                            <ul>
                                <li>Recognize common animals (<strong>domestic and wild</strong>)</li>
                                <li>Improve <strong>visual identification</strong> and <strong>attention to detail</strong></li>
                                <li>Learn <strong>animal names</strong> and <strong>appearances</strong></li>
                                <li>Strengthen <strong>observation</strong> and <strong>categorization</strong> skills</li>
                            </ul>

                            <h2>👩‍🏫 How to Use This Worksheet</h2>
                            <ul>
                                <li>Say the animal name or read the instruction aloud to your child.</li>
                                <li>Ask them to scan the pictures and find the correct one.</li>
                                <li>Let them circle or color the animal they found.</li>
                                <li>Encourage discussion: <strong>“Where have you seen this animal before?”</strong></li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/environmental-science/vegetables/find-vegetable"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Vegetable</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/fruit/find-fruit"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Fruit</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/environmental-science/body-parts/choose-correct"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Identify Correct Body Parts</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why This Activity Is Important</h2>
                            <p>
                                Early science education begins with observation. Identifying animals not only builds vocabulary but also teaches children about their environment, nature, and living things. It&apos;s a fun and gentle way to start exploring the world!
                            </p>
                        </article>
                        <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                        <div
                            ref={containerRef}
                            className="chooseImageContainer"
                            style={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                display: "grid",
                                gridTemplateColumns: `repeat(auto-fit, minmax(${imageSize}px, 1fr))`,
                                gap: "10px",
                                justifyContent: "center",
                                alignItems: "center",
                                overflow: "hidden"
                            }}
                        >
                            {question?.allImages.map((img, index) => (
                                <div key={index} style={{ textAlign: "center" }}>
                                    <img
                                        src={`${process.env.REACT_APP_API_URL}${img}`}
                                        alt={img.replace(".png", "")}
                                        onClick={() => handleImageClick(img)}
                                        style={{
                                            width: `${imageSize}px`,
                                            height: `${imageSize}px`,
                                            cursor: "pointer",
                                            border: selectedImage === img ?
                                                (img === question.correctImage ? "5px solid green" : "5px solid red")
                                                : "none",
                                            transition: "transform 0.2s ease-in-out",
                                        }}
                                    />
                                    {selectedImage === img && (
                                        <div
                                            style={{
                                                fontWeight: "bold",
                                                color: feedback === "Right" ? "green" : "red",
                                                marginTop: "5px",
                                            }}
                                        >
                                            {feedback}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: '150px' }}
                            className="img-fluid"
                            src={process.env.REACT_APP_LOADING_LINK}
                            alt="Well Done"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default FindAnimal;
